import { lazy, Suspense } from 'react';
import { Route, RouterProvider, createBrowserRouter, createRoutesFromElements, Outlet } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';

import RootLayout from '../layout/RootLayout';
import PrivateRoute from '../layout/PrivateRoute';

import ErrorFallBack from '../ErrorFallBack';
import MotionWrapper from '../components/Animation/MotionWrapper';
import { logError } from '@/utils/methods/logger';
import Spinner from '@/components/Elements/Spinner';
import LandingFallback from '@/pages/landing/suspense/LandingFallback';

// --------------------
// Lazy pages (code split)
// --------------------
const LandingPage = lazy(() => import('../pages/landing'));
const Home = lazy(() => import('../pages/home'));
const HomeLayout = lazy(() => import('../layout/HomeLayout'));
const Offers = lazy(() => import('../pages/offer'));

const Menu = lazy(() => import('../pages/menu'));
const TrackFood = lazy(() => import('../pages/trackfood'));
const Checkout = lazy(() => import('../pages/checkout'));
const Payment = lazy(() => import('../pages/payment'));
const Confirm = lazy(() => import('../pages/confirm'));
const PaymentProcess = lazy(() => import('../pages/paymentprocess'));

const ProfileLayout = lazy(() => import('../layout/ProfileLayout'));
const Profile = lazy(() => import('../pages/profile/Profile'));
const Address = lazy(() => import('../pages/profile/Address'));
const Bookmarks = lazy(() => import('../pages/profile/Bookmarks'));
const BankTab = lazy(() => import('../pages/profile/BankTab'));
const PastOrders = lazy(() => import('../pages/profile/PastOrders'));
const Rewards = lazy(() => import('../pages/profile/Rewards'));
const Preference = lazy(() => import('../pages/profile/Preference'));
const Wallet = lazy(() => import('../pages/profile/wallet'));

const Login = lazy(() => import('../pages/auth/Login'));

const FaqPage = lazy(() => import('../pages/faq'));
const About = lazy(() => import('../pages/about'));

const TermsOfUse = lazy(() => import('../pages/termsofuse'));
const TermsConditions = lazy(() => import('../pages/termsconditions'));
const PrivacyPolicy = lazy(() => import('../pages/privacypolicy'));
const AppPrivacyPolicy = lazy(() => import('../pages/appprivacypolicy'));
const Privacy = lazy(() => import('../pages/privacy'));
const AppRefundPolicy = lazy(() => import('../pages/apprefundpolicy'));

const UnsubscribeModal = lazy(() => import('../components/Modal/UnsubscribeModal'));
const UnsubscribeModalTwo = lazy(() => import('../components/Modal/UnsubscribeModalTwo'));
const AccountDeleteModalTwo = lazy(() => import('../components/Modal/AccountDeleteModalTwo'));

const OptOut = lazy(() => import('@/pages/optOut/OptOut'));
const TakeawayDriverAppPrivacyPolicy = lazy(() => import('@/pages/takeawayDriverAppPrivacyPolicy'));
const StoreNotFound = lazy(() => import('@/pages/storeNotFound/StoreNotFound'));
const Payonlineappconfirm = lazy(() => import('@/pages/payonlineappconfirm/Payonlineappconfirm'));

const PageNotFound = lazy(() => import('../pages/PageNotFound'));

// --------------------
// Suspense wrapper (single place)
// --------------------
const LazyPage = ({ children }) => <Suspense fallback={<Spinner />}>{children}</Suspense>;
const LazyLandingPage = ({ children }) => <Suspense fallback={<LandingFallback />}>{children}</Suspense>;

const router = createBrowserRouter(
	createRoutesFromElements(
		<Route>
			<Route
				path="/"
				element={
					<ErrorBoundary FallbackComponent={ErrorFallBack} onError={logError}>
						<RootLayout />
					</ErrorBoundary>
				}
			>
				{/* -------- Landing */}
				<Route
					index
					element={
						<LazyLandingPage>
							<MotionWrapper>
								<LandingPage />
							</MotionWrapper>
						</LazyLandingPage>
					}
				/>

				{/* -------- Takeaways */}
				<Route
					path="/takeaways/*"
					element={
						<LazyPage>
							<MotionWrapper>
								<Home />
							</MotionWrapper>
						</LazyPage>
					}
				/>

				{/* -------- Stores (removed duplicate block) */}
				<Route path="stores" element={<Outlet />}>
					<Route
						index
						element={
							<LazyPage>
								<MotionWrapper>
									<HomeLayout />
								</MotionWrapper>
							</LazyPage>
						}
					/>
					<Route
						path="offers"
						element={
							<LazyPage>
								<MotionWrapper>
									<Offers />
								</MotionWrapper>
							</LazyPage>
						}
					/>
					<Route
						path="placeholder"
						element={
							<LazyPage>
								<MotionWrapper>
									<Offers />
								</MotionWrapper>
							</LazyPage>
						}
					/>
					<Route path=":id" element={<Outlet />}>
						<Route
							index
							element={
								<LazyPage>
									<Home />
								</LazyPage>
							}
						/>
					</Route>
				</Route>

				{/* -------- Order */}
				<Route path="order" element={<Outlet />}>
					<Route path=":id" element={<Outlet />}>
						<Route path=":id" element={<Outlet />}>
							<Route
								index
								element={
									<LazyPage>
										<Menu />
									</LazyPage>
								}
							/>
						</Route>
					</Route>
				</Route>

				{/* -------- About (public) */}
				<Route path="about" element={<Outlet />}>
					<Route path=":id" element={<Outlet />}>
						<Route path=":id" element={<Outlet />}>
							<Route
								index
								element={
									<LazyPage>
										<About />
									</LazyPage>
								}
							/>
						</Route>
					</Route>
				</Route>

				{/* -------- Store (private) */}
				<Route path="store" element={<PrivateRoute element={<Outlet />} />}>
					<Route
						path="cart"
						element={
							<PrivateRoute
								element={
									<LazyPage>
										<Checkout />
									</LazyPage>
								}
							/>
						}
					/>
					<Route
						path="paynow"
						element={
							<PrivateRoute
								element={
									<LazyPage>
										<Checkout />
									</LazyPage>
								}
							/>
						}
					/>
					<Route
						path="payment"
						element={
							<PrivateRoute
								element={
									<LazyPage>
										<Payment />
									</LazyPage>
								}
							/>
						}
					/>

					<Route path="orderstatus" element={<PrivateRoute element={<Outlet />} />}>
						<Route path=":id" element={<PrivateRoute element={<Outlet />} />}>
							<Route
								index
								element={
									<PrivateRoute
										element={
											<LazyPage>
												<TrackFood />
											</LazyPage>
										}
									/>
								}
							/>
						</Route>
					</Route>

					<Route path="confirm" element={<PrivateRoute element={<Outlet />} />}>
						<Route path=":id" element={<PrivateRoute element={<Outlet />} />}>
							<Route
								index
								element={
									<PrivateRoute
										element={
											<LazyPage>
												<Confirm />
											</LazyPage>
										}
									/>
								}
							/>
						</Route>
					</Route>

					<Route path="paymentprocessing" element={<PrivateRoute element={<Outlet />} />}>
						<Route path=":id1" element={<PrivateRoute element={<Outlet />} />}>
							<Route path=":id2" element={<PrivateRoute element={<Outlet />} />}>
								<Route path=":id3" element={<PrivateRoute element={<Outlet />} />}>
									<Route
										index
										element={
											<PrivateRoute
												element={
													<LazyPage>
														<PaymentProcess />
													</LazyPage>
												}
											/>
										}
									/>
								</Route>
							</Route>
						</Route>
					</Route>

					{/* keep your existing duplicates removed for cleanliness */}
					<Route path="about" element={<Outlet />}>
						<Route path=":id" element={<Outlet />}>
							<Route path=":id" element={<Outlet />}>
								<Route
									index
									element={
										<LazyPage>
											<About />
										</LazyPage>
									}
								/>
							</Route>
						</Route>
					</Route>

					<Route path="about" element={<Outlet />}>
						<Route path=":id" element={<Outlet />}>
							<Route
								index
								element={
									<LazyPage>
										<About />
									</LazyPage>
								}
							/>
						</Route>
					</Route>

					<Route
						path="about"
						element={
							<LazyPage>
								<About />
							</LazyPage>
						}
					/>
				</Route>

				{/* -------- Location (private) */}
				<Route path="location" element={<PrivateRoute element={<Outlet />} />}>
					<Route
						path="cart"
						element={
							<PrivateRoute
								element={
									<LazyPage>
										<Checkout />
									</LazyPage>
								}
							/>
						}
					/>
					<Route
						path="paynow"
						element={
							<PrivateRoute
								element={
									<LazyPage>
										<Checkout />
									</LazyPage>
								}
							/>
						}
					/>
					<Route
						path="payment"
						element={
							<PrivateRoute
								element={
									<LazyPage>
										<Payment />
									</LazyPage>
								}
							/>
						}
					/>

					<Route path="orderstatus" element={<PrivateRoute element={<Outlet />} />}>
						<Route path=":id" element={<PrivateRoute element={<Outlet />} />}>
							<Route
								index
								element={
									<PrivateRoute
										element={
											<LazyPage>
												<TrackFood />
											</LazyPage>
										}
									/>
								}
							/>
						</Route>
					</Route>

					<Route path="confirm" element={<PrivateRoute element={<Outlet />} />}>
						<Route path=":id" element={<PrivateRoute element={<Outlet />} />}>
							<Route
								index
								element={
									<PrivateRoute
										element={
											<LazyPage>
												<Confirm />
											</LazyPage>
										}
									/>
								}
							/>
						</Route>
					</Route>

					<Route path="paymentprocessing" element={<PrivateRoute element={<Outlet />} />}>
						<Route path=":id1" element={<PrivateRoute element={<Outlet />} />}>
							<Route path=":id2" element={<PrivateRoute element={<Outlet />} />}>
								<Route path=":id3" element={<PrivateRoute element={<Outlet />} />}>
									<Route
										index
										element={
											<PrivateRoute
												element={
													<LazyPage>
														<PaymentProcess />
													</LazyPage>
												}
											/>
										}
									/>
								</Route>
							</Route>
						</Route>
					</Route>

					<Route
						path="about"
						element={
							<LazyPage>
								<About />
							</LazyPage>
						}
					/>
				</Route>

				{/* -------- Profile (private) */}
				<Route
					path="profile"
					element={
						<PrivateRoute
							element={
								<LazyPage>
									<ProfileLayout />
								</LazyPage>
							}
						/>
					}
				>
					<Route
						index
						element={
							<PrivateRoute
								element={
									<LazyPage>
										<Profile />
									</LazyPage>
								}
							/>
						}
					/>
					<Route
						path="address"
						element={
							<PrivateRoute
								element={
									<LazyPage>
										<Address />
									</LazyPage>
								}
							/>
						}
					/>
					<Route
						path="bookmarks"
						element={
							<PrivateRoute
								element={
									<LazyPage>
										<Bookmarks />
									</LazyPage>
								}
							/>
						}
					/>
					<Route
						path="bankcards"
						element={
							<PrivateRoute
								element={
									<LazyPage>
										<BankTab />
									</LazyPage>
								}
							/>
						}
					/>
					<Route
						path="past-orders"
						element={
							<PrivateRoute
								element={
									<LazyPage>
										<PastOrders />
									</LazyPage>
								}
							/>
						}
					/>
					<Route
						path="rewards"
						element={
							<PrivateRoute
								element={
									<LazyPage>
										<Rewards />
									</LazyPage>
								}
							/>
						}
					/>
					<Route
						path="preference"
						element={
							<PrivateRoute
								element={
									<LazyPage>
										<Preference />
									</LazyPage>
								}
							/>
						}
					/>
					<Route
						path="wallet"
						element={
							<PrivateRoute
								element={
									<LazyPage>
										<Wallet />
									</LazyPage>
								}
							/>
						}
					/>
				</Route>

				{/* -------- FAQ */}
				<Route path="frequently/asked/questions" element={<Outlet />}>
					<Route
						index
						element={
							<LazyPage>
								<FaqPage />
							</LazyPage>
						}
					/>
					<Route path=":faqId" element={<Outlet />}>
						<Route
							index
							element={
								<LazyPage>
									<FaqPage />
								</LazyPage>
							}
						/>
					</Route>
				</Route>

				{/* -------- Modals / misc */}
				<Route
					path="uns"
					element={
						<LazyPage>
							<UnsubscribeModalTwo />
						</LazyPage>
					}
				/>
				<Route
					path="u"
					element={
						<LazyPage>
							<UnsubscribeModalTwo />
						</LazyPage>
					}
				/>
				<Route
					path="/account-delete"
					element={
						<LazyPage>
							<AccountDeleteModalTwo />
						</LazyPage>
					}
				/>
				<Route
					path="unsubscribe"
					element={
						<LazyPage>
							<UnsubscribeModal />
						</LazyPage>
					}
				/>

				{/* -------- Legal pages */}
				<Route
					path="terms-of-use"
					element={
						<LazyPage>
							<TermsOfUse />
						</LazyPage>
					}
				/>
				<Route
					path="terms-conditions"
					element={
						<LazyPage>
							<TermsConditions />
						</LazyPage>
					}
				/>
				<Route
					path="privacy-policy"
					element={
						<LazyPage>
							<PrivacyPolicy />
						</LazyPage>
					}
				/>
				<Route
					path="appprivacypolicy"
					element={
						<LazyPage>
							<AppPrivacyPolicy />
						</LazyPage>
					}
				/>
				<Route
					path="privacy"
					element={
						<LazyPage>
							<Privacy />
						</LazyPage>
					}
				/>
				<Route
					path="apprefundpolicy"
					element={
						<LazyPage>
							<AppRefundPolicy />
						</LazyPage>
					}
				/>

				{/* -------- Extra routes */}
				<Route
					path="merchant-opt-out"
					element={
						<LazyPage>
							<OptOut />
						</LazyPage>
					}
				/>
				<Route
					path="takeaway-driver-app-privacy-policy"
					element={
						<LazyPage>
							<TakeawayDriverAppPrivacyPolicy />
						</LazyPage>
					}
				/>
				<Route
					path="store-not-found"
					element={
						<LazyPage>
							<StoreNotFound />
						</LazyPage>
					}
				/>
				<Route
					path="payonlineappconfirm"
					element={
						<LazyPage>
							<Payonlineappconfirm />
						</LazyPage>
					}
				/>
			</Route>

			{/* -------- Auth routes outside root */}
			<Route
				path="/login"
				element={
					<LazyPage>
						<MotionWrapper>
							<Login />
						</MotionWrapper>
					</LazyPage>
				}
			/>
			<Route
				path="/registration"
				element={
					<LazyPage>
						<MotionWrapper>
							<Login />
						</MotionWrapper>
					</LazyPage>
				}
			/>

			{/* -------- 404 */}
			<Route
				path="*"
				element={
					<LazyPage>
						<PageNotFound />
					</LazyPage>
				}
			/>
		</Route>
	)
);

const RoutePaths = () => {
	return <RouterProvider router={router} fallbackElement={<p>Loading...</p>} />;
};

export default RoutePaths;
